/**
 * Hook: POST /admin/super-admins/{id}/reset-password — issue a NEW temp password
 * to a super-admin and force a reset on next login (RBAC FE WS-D).
 *
 * The target receives a new one-time temporary password by email, their existing
 * sessions are burned, and `requires_password_reset=TRUE` forces a rotation on
 * next login (the forced-change flow fixed in #217 carries this end-to-end).
 *
 * SA-host only; the affordance is hidden under impersonation (#8), but the backend
 * `require_not_impersonating` on the route is the real boundary. Returns 204.
 *
 * Distinct backend error codes (surfaced by the console, never collapsed):
 *   cannot_reset_self  400  — an SA cannot reset their OWN password here (would
 *                             lock the acting SA out mid-session). The console
 *                             also disables the self-row's button cosmetically.
 *   not_found          404  — SA already gone. The copy is non-enumerating.
 *
 * No list-shape change results, but we re-sync the list so the row's
 * "requires_password_reset" pip reflects the new pending-reset state (#7).
 */
import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { apiClient } from "@/lib/apiClient";
import { ApiError } from "@/lib/apiError";
import { saUsersKey } from "./saUserKeys";

interface ResetSuperAdminPasswordVars {
  id: string;
}

export function useResetSuperAdminPassword(): UseMutationResult<
  void,
  ApiError,
  ResetSuperAdminPasswordVars
> {
  const qc = useQueryClient();
  return useMutation<void, ApiError, ResetSuperAdminPasswordVars>({
    mutationFn: async ({ id }) => {
      await apiClient.post(
        `/admin/super-admins/${encodeURIComponent(id)}/reset-password`,
      );
    },
    onSuccess: () => {
      // #7 — re-sync the list so the pending-reset state reflects the backend.
      qc.invalidateQueries({ queryKey: saUsersKey() });
    },
  });
}
